import React from 'react';
import {
  mapStateToProps,
  mapDispatchToProps,
  type InfiniteEditingAreaProps,
} from './infiniteEditingAreaMapper';

import * as util from '#packages/util';
import constants from '#packages/constants';
import styles from './infiniteEditingArea.scss';
import { Text } from '@wix/wix-base-ui';
import {
  TABLET_LAPTOP_WIDTH,
  TABLET_WIDTH,
  DESKTOP_WIDTH,
  WIDE_DESKTOP_WIDTH,
} from '#packages/preview';
import { translate } from '#packages/i18n';
import MobileFrame from '../../mobileFrame/mobileFrame';
import { PreviewFrame } from '../../previewFrame/previewFrame';
import { StageContainer } from '../../stage/stageContainer';
import OpenedPanels from '../../panels/openedPanels';
import Portal from '../../portal/portal';
import { mobileWizard as MobileWizard } from '#packages/mobileEditor';

const InfiniteEditingArea: React.FC<InfiniteEditingAreaProps> = (props) => {
  const editingAreaRef = React.useRef<HTMLDivElement>();

  const getEditingAreaRect = () => {
    const { top, left, height, width, right, bottom } =
      editingAreaRef.current.getBoundingClientRect();
    return { top, left, height, width, right, bottom };
  };

  const updatePositionsInState = () => {
    if (editingAreaRef.current?.getBoundingClientRect) {
      props.refreshEditingAreaPosition(getEditingAreaRect());
    }
  };
  if (!props.performingMouseMoveAction) {
    updatePositionsInState();
  }

  const getPreviewFrameCoverClassName = () => {
    const {
      mobileWizardOpeningState,
      isStageZoomMode,
      isZoomMode,
      isMobileEditor,
      isPreviewMode,
    } = props;
    return util.cx(styles.previewFrameCover, {
      [styles.previewFrameCoverStageZoomMode]: isStageZoomMode,
      [styles.previewFrameCoverMobileEditor]: isMobileEditor,
      [styles.previewFrameCoverPreviewMode]: isPreviewMode,
      [styles.previewFrameCoverMobileWizard]:
        mobileWizardOpeningState ===
        constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.IN_PROGRESS,
      [styles.previewFrameCoverMobileWizardOut]:
        mobileWizardOpeningState ===
        constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.FINISH,
      [styles.desktopZoomModeOldTopBar]:
        isZoomMode && !util.topBar.isNewTopBarEnabled() && !isMobileEditor,
    });
  };

  const getPreviewWidthInfoText = () => {
    const widthValueText = translate('preview_width_label_prefix', {
      width: props.previewWidth,
    });

    const getWidthDescriptionKey = () => {
      if (props.previewWidth === props.baselineWidth) {
        return 'preview_width_label_suffix_editing_size';
      }

      if (props.previewWidth <= TABLET_WIDTH) {
        return 'preview_width_label_suffix_tablet';
      }

      if (props.previewWidth <= TABLET_LAPTOP_WIDTH) {
        return 'preview_width_label_suffix_tablet_laptop';
      }

      if (props.previewWidth <= DESKTOP_WIDTH) {
        return 'preview_width_label_suffix_laptop';
      }

      if (props.previewWidth < WIDE_DESKTOP_WIDTH) {
        return 'preview_width_label_suffix_desktop';
      }

      return 'preview_width_label_suffix_wide_desktop';
    };

    const widthDescription = translate(getWidthDescriptionKey());

    return [widthValueText, widthDescription].join(' • ');
  };

  const getEditingAreaClassName = () => {
    return util.cx(styles.infiniteEditingArea, {
      [styles.noBoxShadow]: props.isTranslationMode || props.isLightboxMode,
      [styles.mobileWizardEditingArea]: props.shouldRenderMobileWizard,
      [styles.infiniteEditingAreaMobileEditor]: props.isMobileEditor,
      [styles.newMobileEditorLayoutWithStageBanner]:
        props.isStageBannerEnabled && props.isMobileEditor,
    });
  };

  const getEditingAreaTopBottom = () => {
    const editingAreaMargins = { top: 0, bottom: 0 };
    if (
      util.sections.isSectionsEnabled() &&
      props.isZoomOut &&
      !props.isMobileEditor
    ) {
      editingAreaMargins.top = 18;
      editingAreaMargins.bottom = 18;
    }

    if (props.isStageBannerEnabled) {
      editingAreaMargins.top += constants.UI.STAGE_BANNER_HEIGHT;
    }
    return editingAreaMargins;
  };

  const getEditingAreaStyle = () => {
    const { shouldCutEditingArea } = props;

    const stageRightMargin = shouldCutEditingArea
      ? constants.UI.EDITING_AREA_RIGHT_MARGIN
      : 0;

    const { x, width } =
      props.leftBarAreaRef.current?.getBoundingClientRect() || {
        x: 0,
        width: 0,
      };

    const { top, bottom } = getEditingAreaTopBottom();

    const propertiesForRegularStage = {
      '--stage-right-margin': `${stageRightMargin}px`,
      '--stage-left': `${x + width}px`,
      '--stage-top': `${top}px`,
      '--stage-bottom': `${bottom}px`,
      '--stage-scrollbar-width': `${util.uiUtils.getScrollbarWidth()}px`,
    };

    return propertiesForRegularStage as React.CSSProperties;
  };

  return (
    <div
      className={util.cx(styles.infiniteEditorLayoutEditingArea, {
        [styles.truncated]: props.shouldCutEditingArea,
      })}
      style={getEditingAreaStyle()}
    >
      <div
        ref={editingAreaRef}
        id={constants.ROOT_COMPS.SELECTOR_ID.EDITING_AREA}
        className={getEditingAreaClassName()}
        data-hook={
          props.isMobileEditor ? 'editing-area-mobile' : 'editing-area'
        }
      >
        {props.isDesktopPreview && (
          <div className={styles.previewWidthInfo}>
            <Text skin="placeholder" size="tiny" shouldTranslate={false}>
              {getPreviewWidthInfoText()}
            </Text>
          </div>
        )}

        {props.isStageBannerEnabled && (
          <props.contributedStageBannerComponent />
        )}

        <div className={getPreviewFrameCoverClassName()}>
          {props.isMobileEditor && <MobileFrame />}

          <PreviewFrame
            styleForPushedStageAndPreviewFrame={
              props.styleForPushedStageAndPreviewFrame
            }
            truncated={props.shouldCutEditingArea}
            mobile={props.isMobileEditor}
            mobileWizard={
              props.mobileWizardOpeningState ===
              constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.IN_PROGRESS
            }
            shouldAnimatePreviewWidth={props.shouldAnimatePreviewWidth}
          />

          {!props.isPreviewMode && !props.shouldRenderMobileWizard && (
            <StageContainer
              styleForPushedStageAndPreviewFrame={
                props.styleForPushedStageAndPreviewFrame
              }
            />
          )}
        </div>

        <OpenedPanels
          helpPanelProps={props.helpPanelProps}
          selectedComponents={props.selectedComponents}
          isMobileEditor={props.isMobileEditor}
        />

        <Portal />

        {props.shouldRenderMobileWizard && <MobileWizard />}
      </div>
    </div>
  );
};
InfiniteEditingArea.displayName = 'InfiniteEditingArea';

export default util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(InfiniteEditingArea);
